import { Award, Gift, Euro, Pencil, Sparkles, Gauge, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

const LoyaltySection = ({ 
  customer, 
  onEditLoyalty, 
  onManageShirts, 
  onManagePrepaid,
  onManageTreatments,
  onGrantSakuraDraw,
  onRemoveSakuraDraw,
  onFillSakuraGauge,
  onAddPayment 
}) => {
  const loyaltyBalance = customer.loyalty_balance || 0;
  const rewardsAvailable = (customer.rewards_earned || 0) - (customer.rewards_used || 0);
  const freeTreatments = customer.free_treatments || 0;
  const sakuraBonusDraws = customer.sakura_bonus_draws || 0;

  return (
    <div className="mt-4 p-3 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg border border-pink-100">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4 text-pink-500" />
          <span className="font-medium text-sm text-pink-700">Programme fidélité</span>
        </div>
        <span className="text-xs text-slate-500">
          Total dépensé: {(customer.total_spent || 0).toFixed(2)}€
        </span>
      </div>
      
      {/* Progress bar */}
      <div className="mb-2">
        <div className="flex justify-between text-xs text-slate-600 mb-1">
          <span className="flex items-center gap-1">
            Progression: <span className={loyaltyBalance < 0 ? "text-red-600 font-medium" : ""}>{loyaltyBalance.toFixed(2)}€</span> / 50€
            <button 
              onClick={() => onEditLoyalty(customer)}
              className="text-pink-500 hover:text-pink-700 ml-1"
              title="Modifier la progression"
            >
              <Pencil className="w-3 h-3" />
            </button>
          </span>
          <span>{(50 - loyaltyBalance).toFixed(2)}€ restants</span>
        </div>
        <Progress 
          value={Math.max(0, (loyaltyBalance / 50) * 100)} 
          className="h-2"
        />
      </div>
      
      {/* Rewards available */}
      {rewardsAvailable > 0 && (
        <div className="flex items-center gap-2 mt-2 p-2 bg-green-100 rounded-lg">
          <Gift className="w-4 h-4 text-green-600" />
          <span className="text-sm font-medium text-green-700">
            {rewardsAvailable} récompense(s) à déduire = {rewardsAvailable * 5}€ de remise
          </span>
        </div>
      )}
      
      {/* Prepaid Shirts */}
      <div className="flex items-center justify-between mt-3 p-2 bg-blue-50 rounded-lg border border-blue-100">
        <div className="flex items-center gap-2">
          <span className="text-lg">👔</span>
          <span className="text-sm font-medium text-blue-700">
            Chemises prépayées: <strong>{customer.prepaid_shirts || 0}</strong>
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onManageShirts(customer)}
          className="h-7 text-xs text-blue-600 border-blue-200 hover:bg-blue-100"
        >
          Gérer
        </Button>
      </div>
      
      {/* Prepaid Balance */}
      <div className="flex items-center justify-between mt-2 p-2 bg-emerald-50 rounded-lg border border-emerald-100">
        <div className="flex items-center gap-2">
          <span className="text-lg">💰</span>
          <span className="text-sm font-medium text-emerald-700">
            Prépaiement: <strong>{(customer.prepaid_balance || 0).toFixed(2)}€</strong>
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onManagePrepaid(customer)}
          className="h-7 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-100"
        >
          Gérer
        </Button>
      </div>
      
      {/* Free Treatments (from Sakura Tree) */}
      <div className="flex items-center justify-between mt-2 p-2 bg-purple-50 rounded-lg border border-purple-100">
        <div className="flex items-center gap-2">
          <span className="text-lg">🧴</span>
          <span className="text-sm font-medium text-purple-700">
            Traitements offerts: <strong>{freeTreatments}</strong>
          </span>
          {freeTreatments > 0 && (
            <span className="text-xs text-purple-500">(Cerisier Sakura)</span>
          )}
        </div>
        {onManageTreatments && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onManageTreatments(customer)}
            className="h-7 text-xs text-purple-600 border-purple-200 hover:bg-purple-100"
          >
            Déduire
          </Button>
        )}
      </div>
      
      {/* Sakura Tree Bonus Draw */}
      <div className="mt-2 p-2 bg-pink-50 rounded-lg border border-pink-100">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-lg">🌸</span>
            <span className="text-sm font-medium text-pink-700">
              Cerisier Sakura
            </span>
            {(() => {
              const hasPlayedThisMonth = customer.has_played_sakura_this_month;
              const availableDraws = sakuraBonusDraws; // Plus de distinction, juste le compteur de tirages
              
              if (hasPlayedThisMonth) {
                if (availableDraws > 0) {
                  return (
                    <>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full mr-1">
                        ✓ Joué ce mois
                      </span>
                      <span className="text-xs bg-pink-200 text-pink-700 px-2 py-0.5 rounded-full">
                        {availableDraws} tirage(s) en réserve
                      </span>
                    </>
                  );
                }
                return (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    ✓ Tirage effectué ce mois
                  </span>
                );
              }
              
              if (availableDraws > 0) {
                return (
                  <span className="text-xs bg-pink-200 text-pink-700 px-2 py-0.5 rounded-full">
                    {availableDraws} tirage(s) disponible(s)
                  </span>
                );
              }
              
              return null;
            })()}
          </div>
          <div className="flex items-center gap-1">
            {onRemoveSakuraDraw && sakuraBonusDraws > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRemoveSakuraDraw(customer)}
                className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-100"
                title="Retirer un tirage (correction)"
              >
                <Minus className="w-3 h-3" />
              </Button>
            )}
            {onGrantSakuraDraw && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onGrantSakuraDraw(customer)}
                className="h-7 text-xs text-pink-600 border-pink-200 hover:bg-pink-100"
                title="Ajouter un tirage (correction manuelle)"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                +1 tirage
              </Button>
            )}
          </div>
        </div>
        
        {/* Sakura Monthly Spending Progress */}
        {typeof customer.monthly_spending !== 'undefined' && (
          <div className="mt-2">
            <div className="flex justify-between text-xs text-slate-600 mb-1">
              <span>Dépenses du mois</span>
              <span className={customer.monthly_spending >= (customer.sakura_spending_goal || 30) ? "text-green-600 font-medium" : ""}>
                {(customer.monthly_spending || 0).toFixed(2)}€ / {customer.sakura_spending_goal || 30}€
              </span>
            </div>
            <Progress 
              value={Math.min((customer.monthly_spending / (customer.sakura_spending_goal || 30)) * 100, 100)} 
              className="h-2"
            />
            {customer.monthly_spending >= (customer.sakura_spending_goal || 30) ? (
              <p className="text-xs text-green-600 mt-1">✓ Objectif atteint ! Tirage attribué le 1er du mois prochain.</p>
            ) : (
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-pink-500">
                  Encore {((customer.sakura_spending_goal || 30) - customer.monthly_spending).toFixed(2)}€ pour le tirage du mois prochain
                </p>
                {onFillSakuraGauge && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onFillSakuraGauge(customer)}
                    className="h-6 text-xs text-green-600 border-green-200 hover:bg-green-100"
                    title="Remplir la jauge à 30€"
                  >
                    <Gauge className="w-3 h-3 mr-1" />
                    Remplir jauge
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <p className="text-xs text-pink-500 italic mt-1 ml-1">⚠️ 1 tirage max par mois • Attribué le 1er si ≥30€ dépensés le mois précédent</p>
      
      {/* Add Payment Button */}
      <Button 
        size="sm" 
        variant="outline" 
        onClick={() => onAddPayment(customer)}
        className="w-full mt-2 text-pink-600 border-pink-200 hover:bg-pink-50"
      >
        <Euro className="w-4 h-4 mr-1" />
        Ajouter un paiement
      </Button>
    </div>
  );
};

export default LoyaltySection;
